// The Subscribe page's price request with its retries (#7027), with no Angular in it so a unit
// test can drive it. Lives beside paypro-price-fetcher.service.ts, its only caller.
import { isRateLimitedError } from './cloud-functions-errors';

export interface CheckoutFetchOutcome<T> {
  /** the last answer the server gave; may still carry an `error` field after every retry */
  answer: T;
  /** true when the sale-code lookup was refused (HTTP 429) and the prices were fetched without it */
  promoRateLimited: boolean;
}

/**
 * Ask for the prices up to `maxAttempts` times, pausing between attempts. Pricing is critical
 * for the Subscribe page, so a failed attempt is retried.
 *
 * A sale code the page remembers is sent with every request. When the server refuses that
 * lookup because this address tried too many codes in the last hour (#7027), the request is sent
 * again at once WITHOUT the code: the buyer still gets the regular prices, and the caller is told
 * so it can say why the code was not applied. Any other failure keeps the code.
 */
export async function fetchCheckoutWithRetriesAsync<T extends object>(
  request: (promoCode: string | undefined) => Promise<T>,
  promoCode: string | undefined,
  opts: { maxAttempts: number; pause: () => Promise<void> },
): Promise<CheckoutFetchOutcome<T>> {
  let code = promoCode;
  let promoRateLimited = false;
  let answer: T | undefined;
  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      answer = await request(code);
      if (!('error' in answer)) break;
      console.warn(`Checkout: Attempt ${attempt}/${opts.maxAttempts} returned error: ${(answer as { error?: unknown }).error}`);
    } catch (err) {
      console.warn(`Checkout: Attempt ${attempt}/${opts.maxAttempts} failed:`, err);
      // if only the sale-code lookup was refused, ask again at once without the code. The
      // attempt is not counted, so this works on the last attempt too; it can happen only once,
      // because the code is gone after it
      if (isRateLimitedError(err) && code) {
        code = undefined;
        promoRateLimited = true;
        attempt--;
        continue;
      }
      if (attempt === opts.maxAttempts) throw err;
    }
    if (attempt < opts.maxAttempts) await opts.pause();
  }
  return { answer: answer!, promoRateLimited };
}
